import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  BarChart3,
  FlaskConical,
  LayoutDashboard,
  Search,
  SendHorizontal,
  ShieldCheck,
} from "lucide-react";
import { adsOptimizerAPI } from "../AdsOptimizer/adsOptimizerAPI";
import { seoAgentAPI } from "../SeoAgent/seoAgentAPI";
import { useAgentVisibility } from "../../context/AgentVisibilityContext";
import { LandingABTab } from "./components";
import "./DashboardHome.scss";

const TABS = [
  { id: "journey", label: "Customer Journey", icon: LayoutDashboard },
  { id: "ads", label: "Ads", icon: BarChart3, feature: "ads" },
  { id: "landing", label: "Landing A/B", icon: FlaskConical, feature: "landing" },
  { id: "seo", label: "SEO", icon: Search, feature: "seo" },
];

function isoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function comparisonRanges() {
  const currentTo = new Date();
  currentTo.setHours(0, 0, 0, 0);
  currentTo.setDate(currentTo.getDate() - 1);
  const currentFrom = new Date(currentTo);
  currentFrom.setDate(currentFrom.getDate() - 6);
  const previousTo = new Date(currentFrom);
  previousTo.setDate(previousTo.getDate() - 1);
  const previousFrom = new Date(previousTo);
  previousFrom.setDate(previousFrom.getDate() - 6);
  return {
    current: { from: isoDate(currentFrom), to: isoDate(currentTo) },
    previous: { from: isoDate(previousFrom), to: isoDate(previousTo) },
  };
}

function compactNumber(value) {
  if (value === null || value === undefined) return "--";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function currency(value, code = "USD") {
  if (value === null || value === undefined) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code || "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function percent(value) {
  if (value === null || value === undefined) return "--";
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function trendSummary(value, label) {
  const numeric = Number(value);
  if (value === null || value === undefined || !Number.isFinite(numeric)) {
    return { tone: "neutral", text: "Comparison data is not available yet" };
  }
  return {
    tone: numeric >= 0 ? "positive" : "negative",
    text: `${numeric >= 0 ? "Up" : "Down"} ${Math.abs(numeric * 100).toFixed(1)}% ${label}`,
  };
}

function PreviewCommand({ type, onSubmit }) {
  const [message, setMessage] = useState("");

  function submit(event) {
    event.preventDefault();
    const nextMessage = message.trim();
    if (nextMessage) onSubmit(nextMessage);
  }

  return (
    <form className="cf-home-command" onSubmit={submit}>
      <textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit(event);
          }
        }}
        rows={1}
        placeholder={`Ask the AI ${type === "ads" ? "Ads" : "SEO"} Agent anything...`}
        aria-label={`Ask the AI ${type === "ads" ? "Ads" : "SEO"} Agent`}
      />
      <div className="cf-home-command-footer">
        <span><ShieldCheck size={14} /> Opens your protected agent conversation</span>
        <button type="submit" disabled={!message.trim()} aria-label="Open agent and send message">
          <SendHorizontal size={18} />
        </button>
      </div>
    </form>
  );
}

function PreviewSkeleton() {
  return (
    <div className="cf-home-preview-skeleton" aria-label="Loading agent overview">
      <span /><span /><span /><span />
    </div>
  );
}

function AdsPreview({ onOpen, onAsk }) {
  const [snapshot, setSnapshot] = useState({ loading: true, account: null, performance: null, error: "" });

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const accountResult = await adsOptimizerAPI.accounts();
        const account = accountResult.data?.[0];
        if (!account) throw new Error("Connect a Google Ads account to activate this overview.");
        const ranges = comparisonRanges();
        const performance = await adsOptimizerAPI.performance(account.id, ranges.current, ranges.previous);
        if (active) setSnapshot({ loading: false, account, performance, error: "" });
      } catch (error) {
        if (active) setSnapshot({ loading: false, account: null, performance: null, error: error.message });
      }
    }
    load();
    return () => { active = false; };
  }, []);

  if (snapshot.loading) return <PreviewSkeleton />;

  const account = snapshot.account;
  const totals = snapshot.performance?.totals;
  const campaigns = (snapshot.performance?.campaigns || [])
    .filter((campaign) => campaign.status === "ENABLED" || Number(campaign.current?.spend) > 0)
    .sort((left, right) => Number(right.current?.spend || 0) - Number(left.current?.spend || 0));
  const efficient = campaigns
    .filter((campaign) => Number(campaign.current?.conversions) > 0 && Number(campaign.current?.cpa) > 0)
    .sort((left, right) => Number(left.current.cpa) - Number(right.current.cpa))[0];
  const inefficient = campaigns
    .filter((campaign) => Number(campaign.current?.spend) > 0)
    .sort((left, right) => Number(right.current?.cpa || 0) - Number(left.current?.cpa || 0))[0];
  const maxSpend = Math.max(1, ...campaigns.slice(0, 5).map((campaign) => Number(campaign.current?.spend || 0)));
  const topAction = efficient
    ? `Validate lead quality for ${efficient.campaignName}, then consider shifting budget toward its ${currency(efficient.current?.cpa, account?.currency_code)} CPA.`
    : "Run an account audit to identify the safest budget and bidding opportunity.";
  const secondaryAction = inefficient
    ? `Review ${inefficient.campaignName} before its next budget cycle; it carries the highest measured CPA in this period.`
    : "Audit conversion tracking before making allocation decisions.";
  const conversionTrend = trendSummary(totals?.changes?.conversions, "conversions vs prior week");

  return (
    <section className="cf-home-agent-preview ads" aria-label="AI Ads Agent preview">
      <div className="cf-home-preview-status">
        <span><i className={account ? "" : "muted"} /> {account ? "Google Ads connected" : "Ads workspace preview"}</span>
        <button type="button" onClick={onOpen}>Open full agent <ArrowUpRight size={15} /></button>
      </div>

      {snapshot.error ? (
        <button type="button" className="cf-home-empty-preview" onClick={onOpen}>
          <BarChart3 size={24} />
          <strong>AI Ads Agent overview</strong>
          <span>{snapshot.error}</span>
          <small>Open the workspace to connect or troubleshoot the account.</small>
        </button>
      ) : (
        <div className="cf-home-preview-grid">
          <button type="button" className="cf-home-performance-card" onClick={onOpen}>
            <div className="cf-home-card-heading">
              <span>Ad spend efficiency</span>
              <small>Last 7 complete days</small>
            </div>
            <strong className="cf-home-big-number">{currency(totals?.current?.spend, account?.currency_code)}</strong>
            <span className={`cf-home-trend ${conversionTrend.tone}`}>{conversionTrend.text}</span>
            <div className="cf-home-bars" aria-label="Campaign spend distribution">
              {campaigns.slice(0, 5).map((campaign) => (
                <span key={campaign.campaignId} title={campaign.campaignName}>
                  <i style={{ height: `${Math.max(18, (Number(campaign.current?.spend || 0) / maxSpend) * 100)}%` }} />
                </span>
              ))}
              {!campaigns.length && [35, 58, 42, 76, 54].map((height) => <span key={height}><i style={{ height: `${height}%` }} /></span>)}
            </div>
            <div className="cf-home-metric-pills">
              <span>CPA {currency(totals?.current?.cpa, account?.currency_code)}</span>
              <span>CVR {percent(totals?.current?.cvr)}</span>
              <span>{compactNumber(totals?.current?.conversions)} conversions</span>
            </div>
          </button>

          <div className="cf-home-action-stack">
            <button type="button" className="cf-home-top-action" onClick={onOpen}>
              <span>Top action</span>
              <strong>{topAction}</strong>
              <small>Evidence checked / human approval protected</small>
            </button>
            <button type="button" className="cf-home-secondary-action" onClick={onOpen}>
              <span>Also worth doing</span>
              <strong>{secondaryAction}</strong>
            </button>
          </div>
        </div>
      )}

      <PreviewCommand type="ads" onSubmit={onAsk} />
    </section>
  );
}

function SeoTrend({ points }) {
  const safePoints = points?.length ? points.slice(-4) : [
    { label: "Week 1", visits: 0 },
    { label: "Week 2", visits: 0 },
    { label: "Week 3", visits: 0 },
    { label: "Week 4", visits: 0 },
  ];
  const values = safePoints.map((point) => Number(point.visits || 0));
  const max = Math.max(...values, 1);
  const coordinates = values.map((value, index) => {
    const x = 14 + index * (422 / Math.max(values.length - 1, 1));
    const y = 112 - (value / max) * 78;
    return `${x},${y}`;
  }).join(" ");
  return (
    <div className="cf-home-seo-chart">
      <svg viewBox="0 0 450 126" role="img" aria-label="Weekly organic traffic trend">
        <defs>
          <linearGradient id="homeSeoFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2f67e8" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#2f67e8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={`14,120 ${coordinates} 436,120`} fill="url(#homeSeoFill)" />
        <polyline points={coordinates} fill="none" stroke="#2f67e8" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        {coordinates.split(" ").map((coordinate) => {
          const [x, y] = coordinate.split(",");
          return <circle key={coordinate} cx={x} cy={y} r="6" fill="#2f67e8" />;
        })}
      </svg>
      <div>{safePoints.map((point, index) => <span key={`${point.label}-${index}`}>{point.label || `Week ${index + 1}`}</span>)}</div>
    </div>
  );
}

function SeoPreview({ onOpen, onAsk }) {
  const [snapshot, setSnapshot] = useState({ loading: true, site: null, overview: null, error: "" });

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        await seoAgentAPI.login();
        const siteResult = await seoAgentAPI.sites();
        const site = siteResult.data?.[0];
        if (!site) throw new Error("Connect a WordPress site to activate this overview.");
        const overviewResult = await seoAgentAPI.overview(site.id);
        if (active) setSnapshot({ loading: false, site, overview: overviewResult.data || null, error: "" });
      } catch (error) {
        if (active) setSnapshot({ loading: false, site: null, overview: null, error: error.message });
      }
    }
    load();
    return () => { active = false; };
  }, []);

  if (snapshot.loading) return <PreviewSkeleton />;

  const overview = snapshot.overview;
  const traffic = overview?.organicTraffic || {};
  const keywords = overview?.topKeywords || [];
  const topAction = overview?.topAction;
  const secondaryAction = overview?.alsoWorthDoing?.[0];
  const trafficTrend = trendSummary(traffic.yoyChange, "vs comparison period");

  return (
    <section className="cf-home-agent-preview seo" aria-label="AI SEO Agent preview">
      <div className="cf-home-preview-status">
        <span><i /> {snapshot.site ? `${snapshot.site.name} connected` : "SEO workspace"}</span>
        <button type="button" onClick={onOpen}>Open full agent <ArrowUpRight size={15} /></button>
      </div>

      {snapshot.error ? (
        <button type="button" className="cf-home-empty-preview" onClick={onOpen}>
          <Search size={24} />
          <strong>AI SEO Agent overview</strong>
          <span>{snapshot.error}</span>
          <small>Open the workspace to connect or troubleshoot the site.</small>
        </button>
      ) : (
        <>
          <div className="cf-home-preview-grid">
            <button type="button" className="cf-home-performance-card" onClick={onOpen}>
              <div className="cf-home-card-heading">
                <span>Organic traffic</span>
                <small>Last 28 days</small>
              </div>
              <strong className="cf-home-big-number">{compactNumber(traffic.visits)} <em>visits</em></strong>
              <span className={`cf-home-trend ${trafficTrend.tone}`}>{trafficTrend.text}</span>
              <SeoTrend points={traffic.weekly} />
            </button>

            <div className="cf-home-action-stack">
              <button type="button" className="cf-home-top-action" onClick={onOpen}>
                <span>Top action</span>
                <strong>{topAction?.detail || topAction?.title || "Review the highest-impact metadata opportunity before publishing."}</strong>
                <small>WordPress draft / approval required</small>
              </button>
              <button type="button" className="cf-home-secondary-action" onClick={onOpen}>
                <span>Also worth doing</span>
                <strong>{secondaryAction?.detail || secondaryAction?.title || "Review page titles and descriptions with below-average search visibility."}</strong>
              </button>
            </div>
          </div>

          <button type="button" className="cf-home-keywords" onClick={onOpen}>
            <span>Top keywords by organic opportunity</span>
            <div>
              {keywords.slice(0, 3).map((keyword, index) => {
                const rawShare = Number(keyword.share || keyword.score || 0);
                const share = rawShare <= 1 ? rawShare * 100 : rawShare;
                return (
                  <span key={`${keyword.keyword}-${index}`}>
                    <strong>{index + 1}. {keyword.keyword}</strong>
                    <i><b style={{ width: `${Math.max(12, Math.min(100, share))}%` }} /></i>
                    <em>{keyword.share ? `${share.toFixed(0)}%` : "Review"}</em>
                  </span>
                );
              })}
              {!keywords.length && <span className="cf-home-keyword-empty">Sync the site to calculate keyword opportunities.</span>}
            </div>
          </button>
        </>
      )}

      <PreviewCommand type="seo" onSubmit={onAsk} />
    </section>
  );
}

export default function AgentDashboard({ children, aiInsightsEnabled = false, onAskInsights }) {
  const navigate = useNavigate();
  const {
    adsAgentEnabled,
    seoAgentEnabled,
    landingAbEnabled,
    loading: agentVisibilityLoading,
  } = useAgentVisibility();
  const [activeTab, setActiveTab] = useState("journey");
  const visibleTabs = TABS.filter(({ feature }) => (
    !feature
    || (feature === "ads" && adsAgentEnabled)
    || (feature === "landing" && landingAbEnabled)
    || (feature === "seo" && seoAgentEnabled)
  ));

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab("journey");
    }
  }, [activeTab, visibleTabs]);

  function openAgent(type, message = "") {
    navigate(type === "ads" ? "/ads-optimizer" : "/seo-agent", {
      state: message ? {
        dashboardAgentMessage: message,
        dashboardAgentHandoffId: `${type}-${Date.now()}`,
      } : null,
    });
  }

  if (agentVisibilityLoading || (!adsAgentEnabled && !seoAgentEnabled && !landingAbEnabled)) {
    return <div className="my-3 p-3 w-full">{children}</div>;
  }

  return (
    <div className="cf-home-experience">
      <nav className="cf-home-tabs" aria-label="Dashboard views">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button key={id} type="button" className={activeTab === id ? "active" : ""} onClick={() => setActiveTab(id)}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>

      {activeTab === "journey" && <section className="cf-home-basic" aria-label="Customer journey analytics">{children}</section>}
      {activeTab === "ads" && <AdsPreview onOpen={() => openAgent("ads")} onAsk={(message) => openAgent("ads", message)} />}
      {activeTab === "landing" && (
        <LandingABTab
          aiInsightsEnabled={aiInsightsEnabled}
          onAskInsights={onAskInsights}
          onOpenTests={() => navigate("/goals")}
        />
      )}
      {activeTab === "seo" && <SeoPreview onOpen={() => openAgent("seo")} onAsk={(message) => openAgent("seo", message)} />}
    </div>
  );
}
